import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import Button from 'react-bootstrap/Button';

import COPY from '../../../components/PublicSite/copy.json';
import { GROUPS, FIGURES, hasVars } from './groups';

import s from './WebsiteContent.css';
import bt from '../../../components/commonStyle.css';

/*
 * Editor teks & foto situs publik.
 *
 * Tanpa redux-form dan tanpa Apollo, sengaja: form ini bisa punya 596 field, dan
 * mendaftarkan semuanya ke redux-form berarti 596 subscriber redux untuk sesuatu
 * yang cuma perlu state lokal dan satu mutation. Ambil datanya lewat fetch biasa
 * ke /graphql yang sama-origin.
 *
 * Hanya kelompok yang dibuka yang dirender. Merender 596 <textarea> sekaligus
 * membuat halaman ini berat tanpa alasan — client hanya pernah membuka satu
 * bagian dalam satu waktu.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q_GET = '{ getSiteCopy { status results { key en ar } } }';
const M_SET = `mutation ($items: [SiteCopyInput]!) {
  updateSiteCopy(items: $items) { status errorMessage }
}`;

// Awalan sebuah kunci = segmen pertama sebelum titik. 'ch' dan 'ct' dua
// kelompok berbeda meski dua-duanya halaman Contact — memang begitu di copy.json.
const prefixOf = key => key.split('.')[0];

class WebsiteContent extends React.Component {

  state = {
    loading: true,
    open: null,        // awalan kelompok yang sedang dibuka
    override: {},      // { key: {en, ar} } dari DB
    draft: {},         // { key: {en, ar} } yang sedang diedit
    saving: false,
    savedAt: null,
    error: null,
    uploading: null
  };

  componentDidMount() { this.load(); }

  load = async () => {
    const json = await gql(Q_GET);
    const rows = (json.data && json.data.getSiteCopy && json.data.getSiteCopy.results) || [];
    const override = {};
    rows.forEach(r => { override[r.key] = { en: r.en || '', ar: r.ar || '' }; });
    this.setState({ override, loading: false });
  };

  toggle = prefix => this.setState(st => ({
    open: st.open === prefix ? null : prefix,
    draft: {},
    savedAt: null,
    error: null
  }));

  edit = (key, lang, value) => this.setState(st => {
    const base = st.draft[key] || st.override[key] || { en: '', ar: '' };
    return { draft: { ...st.draft, [key]: { ...base, [lang]: value } }, savedAt: null };
  });

  valueOf = (key, lang) => {
    const { draft, override } = this.state;
    if (draft[key] && draft[key][lang] !== undefined) return draft[key][lang];
    if (override[key]) return override[key][lang] || '';
    return '';
  };

  save = async () => {
    const { draft } = this.state;
    const items = Object.keys(draft).map(key => ({
      key, en: draft[key].en || '', ar: draft[key].ar || ''
    }));
    if (!items.length) return;

    this.setState({ saving: true, error: null });
    const json = await gql(M_SET, { items });
    const res = json.data && json.data.updateSiteCopy;

    if (!res || res.status !== 200) {
      this.setState({ saving: false, error: (res && res.errorMessage) || 'Save failed.' });
      return;
    }

    // Terapkan ke state lokal supaya form tidak perlu memuat ulang; baris yang
    // dikosongkan dua-duanya dihapus, mencerminkan apa yang dilakukan mutation.
    this.setState(st => {
      const override = { ...st.override };
      items.forEach(i => {
        if (!i.en.trim() && !i.ar.trim()) delete override[i.key];
        else override[i.key] = { en: i.en, ar: i.ar };
      });
      return { override, draft: {}, saving: false, savedAt: Date.now() };
    });
  };

  upload = async (slot, file) => {
    if (!file) return;
    this.setState({ uploading: slot, error: null });

    const body = new FormData();
    body.append('file', file);
    const resp = await fetch('/uploadSiteFigure', { method: 'post', body, credentials: 'include' })
      .then(r => r.json())
      .catch(() => ({ status: 400 }));

    if (resp.status !== 200) {
      this.setState({ uploading: null, error: 'Upload failed.' });
      return;
    }

    const key = 'figure.' + slot;
    await gql(M_SET, { items: [{ key, en: resp.fileName, ar: '' }] });
    this.setState(st => ({
      override: { ...st.override, [key]: { en: resp.fileName, ar: '' } },
      uploading: null
    }));
  };

  resetFigure = async slot => {
    const key = 'figure.' + slot;
    await gql(M_SET, { items: [{ key, en: '', ar: '' }] });
    this.setState(st => {
      const override = { ...st.override };
      delete override[key];
      return { override };
    });
  };

  renderGroup(prefix, label, keys) {
    const { open, draft, saving, savedAt, error } = this.state;
    const overridden = keys.filter(k => this.state.override[k]).length;

    return (
      <div className={s.group} key={prefix}>
        <button type="button" className={s.groupHead} onClick={() => this.toggle(prefix)}>
          <span>{label}</span>
          <span className={s.count}>
            {keys.length} texts{overridden ? ` · ${overridden} edited` : ''} {open === prefix ? '▲' : '▼'}
          </span>
        </button>

        {open === prefix && (
          <div className={s.groupBody}>
            {keys.map(key => {
              const def = COPY['en-US'][key] || '';
              const defAr = (COPY.ar && COPY.ar[key]) || '';
              const isOver = !!this.state.override[key];
              return (
                <div className={s.row} key={key}>
                  <div className={s.key}>
                    {key}
                    {hasVars(def) && <div><b>keep {def.match(/\{[a-z]+\}/gi).join(' ')}</b></div>}
                  </div>
                  <div className={cx(s.field, isOver && s.overridden)}>
                    <label className={s.fieldLabel}>English</label>
                    <textarea
                      rows={def.length > 90 ? 3 : 1}
                      placeholder={def}
                      value={this.valueOf(key, 'en')}
                      onChange={e => this.edit(key, 'en', e.target.value)}
                    />
                  </div>
                  <div className={cx(s.field, s.rtl, isOver && s.overridden)}>
                    <label className={s.fieldLabel}>Arabic</label>
                    <textarea
                      rows={def.length > 90 ? 3 : 1}
                      placeholder={defAr}
                      value={this.valueOf(key, 'ar')}
                      onChange={e => this.edit(key, 'ar', e.target.value)}
                    />
                  </div>
                </div>
              );
            })}

            <div className={s.actions}>
              <Button className={bt.btnPrimary} disabled={saving || !Object.keys(draft).length}
                onClick={this.save}>
                {saving ? 'Saving…' : 'Save this section'}
              </Button>
              {savedAt && <span className={s.saved}>Saved. Live on the website within a minute.</span>}
              {error && <span className={bt.errorMessage}>{error}</span>}
            </div>
          </div>
        )}
      </div>
    );
  }

  renderFigures() {
    const { open, uploading } = this.state;
    if (open !== '__figures') {
      return (
        <div className={s.group} key="figures">
          <button type="button" className={s.groupHead} onClick={() => this.toggle('__figures')}>
            <span>Photos</span>
            <span className={s.count}>{FIGURES.length} images ▼</span>
          </button>
        </div>
      );
    }

    return (
      <div className={s.group} key="figures">
        <button type="button" className={s.groupHead} onClick={() => this.toggle('__figures')}>
          <span>Photos</span>
          <span className={s.count}>{FIGURES.length} images ▲</span>
        </button>
        <div className={s.groupBody}>
          <div className={s.figGrid}>
            {FIGURES.map(f => {
              const over = this.state.override['figure.' + f.slot];
              const name = (over && over.en) || f.slot;
              return (
                <div className={s.figCard} key={f.slot}>
                  <div className={s.figName}>{f.label}</div>
                  <img className={s.figThumb} src={`/images/site/${name}-800.webp`} alt="" />
                  <input type="file" accept="image/*" disabled={uploading === f.slot}
                    onChange={e => this.upload(f.slot, e.target.files[0])} />
                  {over && (
                    <Button className={bt.btnSecondary} style={{ marginTop: 8 }}
                      onClick={() => this.resetFigure(f.slot)}>Use default</Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading } = this.state;
    if (loading) return <div className={s.root}>Loading…</div>;

    const all = Object.keys(COPY['en-US']);
    const known = GROUPS.map(g => g.prefix);
    // Awalan yang ada di copy.json tapi belum diberi nama tetap muncul, memakai
    // awalannya sendiri — supaya teks baru tidak pernah diam-diam tak terjangkau.
    const extras = Array.from(new Set(all.map(prefixOf))).filter(p => known.indexOf(p) < 0);
    const groups = GROUPS.concat(extras.map(p => ({ prefix: p, label: p })));

    return (
      <div className={s.root}>
        <div className={s.container}>
          <div className={s.paddingRoutesSection}>
            <h1 className={bt.titleText}>Website Content</h1>
            <p className={s.intro}>
              Text and photos on the public website (basrecovery.com). Leave a box empty to
              use the built-in wording — the grey text inside each box is what visitors see
              today. Privacy Policy and Terms are edited under Static Page, not here.
            </p>
            {groups.map(g => {
              const keys = all.filter(k => prefixOf(k) === g.prefix);
              return keys.length ? this.renderGroup(g.prefix, g.label, keys) : null;
            })}
            {this.renderFigures()}
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(s, bt)(WebsiteContent);
